import { DiaryLine } from './DiaryLine';
import { tagColor, tagBackgroundColor } from '@/utils/tagTheme';

function SavingsChip({ tag }: { tag: string }) {
  return (
    <span
      className="whitespace-nowrap rounded-sm font-mono text-xs font-medium"
      style={{
        color: tagColor(tag, 'light'),
        backgroundColor: tagBackgroundColor(tag, 'light'),
        padding: '0 0.3em',
      }}
    >
      #{tag}
    </span>
  );
}

/** Right-aligned fixed-width price so the decimals line up down the column.
 *  6ch fits the widest price ("$29.99"); the font is mono so ch is exact. */
function Price({ children }: { children: string }) {
  return <span className="inline-block w-[6ch] text-right text-foreground">{children}</span>;
}

function CheckMark() {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 16 16"
      className="inline-block h-4 w-4 stroke-foreground"
      fill="none"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M3 8.5 6.5 12 13 4.5" />
    </svg>
  );
}

type Cell = boolean | string;

/** Every claim mirrors copy already published on this page or /features. */
const featureRows: Array<{ label: string; free: Cell; pro: Cell }> = [
  { label: 'unlimited notes, offline', free: true, pro: true },
  { label: 'export to image, text, URL', free: true, pro: true },
  { label: 'themes', free: 'light & dark', pro: 'thirteen' },
  { label: 'backup & sync across devices', free: false, pro: true },
];

function FeatureCell({ value }: { value: Cell }) {
  if (value === true) {
    return (
      <>
        <CheckMark />
        <span className="sr-only">included</span>
      </>
    );
  }
  if (value === false) {
    return (
      <>
        <span aria-hidden className="text-muted-foreground/50">
          –
        </span>
        <span className="sr-only">not included</span>
      </>
    );
  }
  return <span className="text-muted-foreground">{value}</span>;
}

export function Pricing() {
  return (
    <div className="mt-20 sm:mt-28 md:grid md:grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)] md:items-center md:gap-12">
      <div>
        <DiaryLine time="17:02">
          <h2 className="text-2xl font-semibold tracking-tight text-foreground sm:text-[1.75rem]">
            free forever
          </h2>
        </DiaryLine>

        <DiaryLine className="mt-3">
          <p className="max-w-[58ch] text-pretty text-base leading-relaxed text-muted-foreground sm:text-lg">
            the free tier is the whole product. pro adds backup and sync across
            devices, plus eleven more themes.
          </p>
        </DiaryLine>

        <DiaryLine className="mt-6">
          <ul role="list" className="m-0 list-none space-y-1 pl-0 font-mono text-sm leading-[1.75]">
            <li>
              <Price>$2.99</Price>
              <span className="text-muted-foreground"> / month</span>
            </li>
            <li>
              <Price>$29.99</Price>
              <span className="text-muted-foreground"> / year </span>
              <SavingsChip tag="2-months-free" />
            </li>
            <li>
              <Price>$59.99</Price>
              <span className="text-muted-foreground"> / lifetime</span>
            </li>
          </ul>
        </DiaryLine>
      </div>

      <div className="mt-8 min-w-0 md:mt-0">
        <table className="w-full border-collapse font-mono text-sm">
          <thead>
            <tr className="border-b border-border">
              <th scope="col" className="sr-only">
                feature
              </th>
              <th scope="col" className="w-[5.5rem] pb-3 text-center font-semibold text-foreground sm:w-28">
                free
              </th>
              <th scope="col" className="w-[5.5rem] pb-3 text-center sm:w-28">
                <span className="rounded-sm bg-foreground px-1.5 py-0.5 font-mono text-[10px] font-medium tracking-wider text-background">
                  PRO
                </span>
              </th>
            </tr>
          </thead>
          <tbody>
            {featureRows.map((row) => (
              <tr key={row.label} className="border-b border-border/60">
                <th scope="row" className="py-2.5 pr-4 text-left font-normal text-foreground">
                  {row.label}
                </th>
                <td className="py-2.5 text-center">
                  <FeatureCell value={row.free} />
                </td>
                <td className="py-2.5 text-center">
                  <FeatureCell value={row.pro} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
